import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import { entityKeys } from "@/entities/api";
import type { AccessLevel, TableAccess } from "@/api/me";

export type { AccessLevel, TableAccess };

/* ------------------------------------------------------- shared catalog */
export interface LevelOption {
  value: AccessLevel;
  label: string;
  description: string | null;
}

/** Which privileges a table accepts (Read is always supported). */
export interface TableSupports {
  create: boolean;
  write: boolean;
  delete: boolean;
}

export interface CatalogTable {
  entity: string;
  label: string;
  supports: TableSupports;
}

export interface CatalogArea {
  area: string;
  tables: CatalogTable[];
}

/** Additional privilege or column-security code. */
export interface PrivilegeCode {
  code: string;
  description: string | null;
}

export interface PrivilegeCatalog {
  levels: LevelOption[];
  areas: CatalogArea[];
  additional: PrivilegeCode[];
  columns: PrivilegeCode[];
}

/* GET /api/admin/access/matrix */
export interface RoleSummary {
  id: string;
  code: string;
  name: string;
  description: string | null;
  is_staff_role: boolean;
  /** Built-in role: can be edited but not deleted. */
  seeded: boolean;
  user_count: number;
  entra_groups: string[];
  /** Additional + column-security codes. */
  permissions: string[];
  /** Only entities with a privilege row. */
  tables: Record<string, TableAccess>;
}

export interface AccessMatrix extends PrivilegeCatalog {
  roles: RoleSummary[];
}

/* GET/PUT /api/admin/roles/{id} */
export interface RoleTablePrivilege extends TableAccess {
  entity: string;
  label: string;
  area: string;
  supports: TableSupports;
}

export interface RoleDetail extends RoleSummary, PrivilegeCatalog {
  /** Every table, in area order. */
  table_privileges: RoleTablePrivilege[];
  /** People who have the role, and the Entra group(s) giving it to them. */
  members?: RoleMember[];
  /** Menu items ("<workspace>/<section>") the role hides. */
  hidden_nav?: string[];
}

export interface RoleMember {
  user_id: string;
  display_name: string;
  email: string | null;
  last_sign_in_at: string | null;
  /** Entra group names, and/or "Assigned manually". */
  via: string[];
  /** Assigned directly in Pulse (removable here). */
  manual?: boolean;
  /** The Entra groups giving the role (removed in Entra, not here). */
  from_groups?: string[];
}

export interface EntraSyncResult {
  groups: number;
  members: number;
  users_created: number;
  users_updated: number;
  users_cleared: number;
  group_errors: string[];
}

export interface RoleUpdate {
  name?: string;
  description?: string | null;
  /** Replaces every table privilege for the role. */
  tables?: (TableAccess & { entity: string })[];
  /** Replaces the additional + column-security codes. */
  permissions?: string[];
  /** Replaces the menu items the role hides. */
  hidden_nav?: string[];
}

export interface RoleCreate {
  name: string;
  description?: string | null;
  copy_from?: string;
}

/* GET /api/admin/access/users/{user_id} */
export interface UserAccessRole {
  id: string;
  name: string;
  code: string;
  via: string | null;
}

export interface UserClientAccess {
  client: string;
  department: string | null;
  sees_invoices: boolean;
  via: string | null;
}

export interface UserTableAccess extends TableAccess {
  entity: string;
  label: string;
  area: string;
  via: string | null;
}

export interface UserPermission {
  code: string;
  description: string | null;
  via_roles: string[];
}

export interface UserAccess {
  user_id: string;
  display_name: string;
  is_staff: boolean;
  last_sign_in_at: string | null;
  roles_refreshed_at: string | null;
  roles: UserAccessRole[];
  client_access: UserClientAccess[];
  tables: UserTableAccess[];
  /** Additional + column-security codes. */
  permissions: UserPermission[];
  note: string | null;
}

/* GET/PUT /api/admin/access-groups/{id} */
export type AccessGroupKind = "role" | "client_scope" | "ignored";

export interface NamedRef {
  id: string;
  name: string;
}

export interface AccessGroupMember {
  user_id: string;
  display_name: string;
  email: string | null;
  /** Role names this group gives the person. */
  roles: string[];
  /** "Client" or "Client / Department" for client groups. */
  client_access: string | null;
}

export interface AccessGroupDetail {
  id: string;
  name: string;
  entra_group_id: string;
  kind: AccessGroupKind;
  kind_help: string;
  roles: (NamedRef & { is_staff_role: boolean })[];
  client: NamedRef | null;
  department: NamedRef | null;
  grants_portal_financials: boolean;
  notes: string | null;
  members: AccessGroupMember[];
  available_roles: NamedRef[];
}

/* GET /api/admin/access-groups */
export interface AccessGroupSummary {
  id: string;
  name: string;
  entra_group_id: string;
  kind: AccessGroupKind;
  roles: (NamedRef & { is_staff_role: boolean })[];
  client: NamedRef | null;
  department: NamedRef | null;
  grants_portal_financials: boolean;
  notes: string | null;
  /** Distinct people holding a role or client scope through the group. */
  member_count: number;
}

/** Partial update; `role_ids` is the full list of roles the group gives. */
export interface AccessGroupUpdate {
  name?: string;
  kind?: AccessGroupKind;
  client_id?: string | null;
  department_id?: string | null;
  grants_portal_financials?: boolean;
  notes?: string | null;
  role_ids?: string[];
}

/* GET /api/admin/access/overview */
/** A role, access group or user, with the access it adds up to (as when signed in). */
export interface AccessSubject {
  id: string;
  name: string;
  /** Role description, group's client / department, or user's email. */
  detail: string | null;
  is_staff: boolean;
  /** The roles giving the access (a role: itself). */
  role_ids: string[];
  /** Readable tables only; a missing entity means no access. */
  tables: Record<string, TableAccess>;
  /** Additional + column-security codes, plus derived `<workspace>.access` codes for the menu. */
  permissions: string[];
  /** Menu items hidden by every one of the roles. */
  hidden_nav: string[];
}

export interface AccessGroupSubject extends AccessSubject {
  kind: AccessGroupKind;
}

/** One of a user's roles and where it comes from. */
export interface AccessGrant {
  role_id: string;
  /** The access group giving the role; null when assigned manually. */
  group_id: string | null;
  /** The group's name, or "Assigned manually". */
  group: string;
}

export interface AccessUserSubject extends AccessSubject {
  is_active: boolean;
  /** Entra groups giving the roles, and/or "Assigned manually". */
  via: string[];
  grants: AccessGrant[];
}

export interface AccessOverview extends PrivilegeCatalog {
  roles: AccessSubject[];
  groups: AccessGroupSubject[];
  users: AccessUserSubject[];
}

/* ---------------------------------------------------------------- hooks */
const matrixKey = ["admin", "access", "matrix"] as const;
const overviewKey = ["admin", "access", "overview"] as const;
const roleKey = (id: string) => ["admin", "roles", id] as const;
// Under ["admin", "access"] so saving a group (which invalidates that prefix) refreshes the cards too.
const groupsKey = ["admin", "access", "groups"] as const;

export function useAccessMatrix() {
  return useQuery({
    queryKey: matrixKey,
    queryFn: ({ signal }) => apiFetch<AccessMatrix>("/admin/access/matrix", { signal }),
  });
}

export function useAccessOverview() {
  return useQuery({
    queryKey: overviewKey,
    queryFn: ({ signal }) => apiFetch<AccessOverview>("/admin/access/overview", { signal }),
  });
}

export function useRole(id: string) {
  return useQuery({
    queryKey: roleKey(id),
    queryFn: ({ signal }) => apiFetch<RoleDetail>(`/admin/roles/${encodeURIComponent(id)}`, { signal }),
  });
}

/** After any role change: the matrix and per-user access views are stale. */
function useInvalidateAccess() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: matrixKey });
    void qc.invalidateQueries({ queryKey: overviewKey });
    void qc.invalidateQueries({ queryKey: ["admin", "access", "user"] });
    void qc.invalidateQueries({ queryKey: groupsKey });
  };
}

export function useUpdateRole(id: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateAccess();
  return useMutation({
    mutationFn: (body: RoleUpdate) =>
      apiFetch<RoleDetail>(`/admin/roles/${encodeURIComponent(id)}`, { method: "PUT", body }),
    onSuccess: (role) => {
      qc.setQueryData(roleKey(id), role);
      invalidate();
    },
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  const invalidate = useInvalidateAccess();
  return useMutation({
    mutationFn: (body: RoleCreate) => apiFetch<RoleDetail>("/admin/roles", { method: "POST", body }),
    onSuccess: (role) => {
      qc.setQueryData(roleKey(role.id), role);
      invalidate();
    },
  });
}

/** Refresh everyone's roles from Entra group membership (Microsoft Graph). */
export function useEntraSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<EntraSyncResult>("/admin/access/entra-sync", { method: "POST" }),
    // Roles, user lists and role columns all change: refetch everything on screen.
    onSuccess: () => void qc.invalidateQueries(),
  });
}

export function useDeleteRole(id: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateAccess();
  return useMutation({
    mutationFn: () => apiFetch<void>(`/admin/roles/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: roleKey(id) });
      invalidate();
    },
  });
}

export function useUserAccess(userId: string) {
  return useQuery({
    queryKey: ["admin", "access", "user", userId],
    queryFn: ({ signal }) => apiFetch<UserAccess>(`/admin/access/users/${encodeURIComponent(userId)}`, { signal }),
  });
}

/* -------------------------------------------------------------- helpers */
/** "finance.invoice:generate" → "finance". */
export function permissionArea(code: string): string {
  return code.split(/[.:]/)[0] || "other";
}

export function areaLabel(area: string): string {
  if (area.toLowerCase() === "crm") return "CRM";
  return area.charAt(0).toUpperCase() + area.slice(1).replace(/_/g, " ");
}

/** Group items by area, keeping first-seen order. */
export function groupByArea<T>(items: T[], areaOf: (item: T) => string): { area: string; items: T[] }[] {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const a = areaOf(item);
    const list = map.get(a);
    if (list) list.push(item);
    else map.set(a, [item]);
  }
  return [...map].map(([area, list]) => ({ area, items: list }));
}

/* -------------------------------------------------------- access groups */
const groupKey = (id: string) => ["admin", "access-groups", id] as const;

/** Every access group with what it gives and its member count (the Access groups cards). */
export function useAccessGroups() {
  return useQuery({
    queryKey: groupsKey,
    queryFn: ({ signal }) => apiFetch<AccessGroupSummary[]>("/admin/access-groups", { signal }),
  });
}

export function useAccessGroup(id: string) {
  return useQuery({
    queryKey: groupKey(id),
    queryFn: ({ signal }) => apiFetch<AccessGroupDetail>(`/admin/access-groups/${encodeURIComponent(id)}`, { signal }),
  });
}

export function useUpdateAccessGroup(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AccessGroupUpdate) =>
      apiFetch<AccessGroupDetail>(`/admin/access-groups/${encodeURIComponent(id)}`, { method: "PUT", body }),
    onSuccess: (group) => {
      qc.setQueryData(groupKey(id), group);
      // Saving re-applies the group to its members: roles, user access and the generic lists change.
      void qc.invalidateQueries({ queryKey: ["admin", "access"] });
      void qc.invalidateQueries({ queryKey: ["admin", "roles"] });
      void qc.invalidateQueries({ queryKey: entityKeys.entity("access_group") });
    },
  });
}

/** A person in Entra ID found by the directory search (for adding to an access group). */
export interface DirectoryUser {
  entra_object_id: string;
  display_name: string;
  email: string | null;
  is_guest: boolean;
  enabled: boolean;
}

/** Search Entra ID for people to add to an access group (needs Graph User.Read.All on the server). */
export function useDirectorySearch(groupId: string, search: string) {
  const term = search.trim();
  return useQuery({
    queryKey: ["admin", "access-groups", groupId, "directory", term] as const,
    queryFn: ({ signal }) =>
      apiFetch<DirectoryUser[]>(`/admin/access-groups/${encodeURIComponent(groupId)}/directory`, {
        signal,
        query: { search: term },
      }),
    enabled: term.length >= 2,
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * Add or remove people in an access group. The change is made in Entra ID itself (Microsoft Graph) and applied in
 * Pulse straight away; the response is the updated group.
 */
export function useAccessGroupMembers(groupId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateAccess();
  const onSuccess = (group: AccessGroupDetail) => {
    qc.setQueryData(groupKey(groupId), group);
    invalidate();
  };
  const base = `/admin/access-groups/${encodeURIComponent(groupId)}/members`;
  return {
    add: useMutation({
      mutationFn: (user: DirectoryUser) =>
        apiFetch<AccessGroupDetail>(base, {
          method: "POST",
          body: {
            entra_object_id: user.entra_object_id,
            display_name: user.display_name,
            email: user.email,
            is_guest: user.is_guest,
          },
        }),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (userId: string) =>
        apiFetch<AccessGroupDetail>(`${base}/${encodeURIComponent(userId)}`, { method: "DELETE" }),
      onSuccess,
    }),
  };
}
